import type { TFunction } from 'i18next';
import {
  useId,
  type ComponentProps,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { AlertIcon, CheckIcon, ChevronDownIcon, ClockIcon } from './icons.js';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'soft';

/**
 * Buttons of the sheet (design/foundation/components.css, SP-UX10.1). Every standalone action
 * looks like a button (T-47); "soft" is the quiet one, as the sheet has no third look.
 */
const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  ghost: 'btn-ghost',
  danger: 'btn-danger',
  soft: 'btn-secondary',
};

/** Classes of a button, also for <a> and <Link> that navigate. */
type ButtonSize = 'md' | 'sm' | 'lg';

export function buttonClass(variant: ButtonVariant = 'primary', size: ButtonSize = 'md') {
  return `btn ${BUTTON_STYLES[variant]}${size === 'sm' ? ' btn-sm' : size === 'lg' ? ' btn-lg' : ''}`;
}

/**
 * A button; `busy` while its action runs. Busy, it is not disabled – Chrome drops the focus of a
 * button that gets disabled to the page, after every save (SP-A2.185) – but said to be busy and
 * not to be pressed (aria-busy, aria-disabled; drawn as disabled), and a click does nothing: not
 * even the Enter in a field of its form, which the browser sends as a click to its button.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  busy = false,
  className = '',
  children,
  onClick,
  ...props
}: ComponentProps<'button'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      {...props}
      aria-disabled={busy || props['aria-disabled']}
      aria-busy={busy || undefined}
      onClick={(e) => {
        if (busy) e.preventDefault();
        else onClick?.(e);
      }}
      className={`${buttonClass(variant, size)} ${className}`}
    >
      {children}
    </button>
  );
}

/** A link that moves to another page, drawn as a button (T-47). */
export function LinkButton({
  variant = 'soft',
  size = 'md',
  className = '',
  ...props
}: ComponentProps<typeof Link> & { variant?: ButtonVariant; size?: ButtonSize }) {
  return <Link {...props} className={`${buttonClass(variant, size)} ${className}`} />;
}

interface FieldProps {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  optional?: boolean;
}

export function TextField({
  label,
  hint,
  error,
  optional,
  large = false,
  note,
  id,
  ...props
}: FieldProps & {
  /** The big field of the sheet (.field-lg), e.g. the address of the Quick-Check. */
  large?: boolean;
  /** A line between the control and the hint, read with the field (checkout.html: .vies). */
  note?: ReactNode;
} & ComponentProps<'input'>) {
  const { t } = useTranslation();
  const auto = useId();
  const fieldId = id ?? auto;
  const describedBy =
    [
      note ? `${fieldId}-note` : null,
      hint ? `${fieldId}-hint` : null,
      error ? `${fieldId}-error` : null,
    ]
      .filter(Boolean)
      .join(' ') || undefined;
  return (
    <div className={`field${large ? ' field-lg' : ''}${error ? ' err' : ''}`}>
      <label htmlFor={fieldId}>
        {label}
        {optional ? ` (${t('app.optional')})` : null}
      </label>
      <input
        id={fieldId}
        {...props}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
      />
      {note ? <div id={`${fieldId}-note`}>{note}</div> : null}
      {hint ? (
        <p id={`${fieldId}-hint`} className="hint">
          {hint}
        </p>
      ) : null}
      {error ? <FieldError id={`${fieldId}-error`}>{error}</FieldError> : null}
    </div>
  );
}

/** A text area with its label (sheet: .field with a textarea). */
export function TextAreaField({
  label,
  id,
  ...props
}: { label: ReactNode } & ComponentProps<'textarea'>) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <div className="field">
      <label htmlFor={fieldId}>{label}</label>
      <textarea id={fieldId} {...props} />
    </div>
  );
}

/** The error line under a field: icon and text, announced with the field (aria-describedby). */
function FieldError({ id, children }: { id: string; children: ReactNode }) {
  return (
    <p
      id={id}
      className="error flex items-start gap-1 [&>svg]:mt-px [&>svg]:size-4 [&>svg]:shrink-0"
    >
      <AlertIcon />
      {children}
    </p>
  );
}

export function SelectField({
  label,
  hint,
  error,
  labelHidden = false,
  id,
  children,
  ...props
}: FieldProps & {
  /** The label for screen readers only: a select in a row whose context names it (admin/team.html). */
  labelHidden?: boolean;
} & SelectHTMLAttributes<HTMLSelectElement>) {
  const auto = useId();
  const fieldId = id ?? auto;
  const describedBy =
    [hint ? `${fieldId}-hint` : null, error ? `${fieldId}-error` : null]
      .filter(Boolean)
      .join(' ') || undefined;
  return (
    <div className={`field${error ? ' err' : ''}`}>
      <label htmlFor={fieldId} className={labelHidden ? 'sr-only' : undefined}>
        {label}
      </label>
      <div className="relative">
        <select
          id={fieldId}
          {...props}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          // Busy (a change saving, SP-A2.186): drawn as disabled, yet enabled – the focus stays.
          className="wg-select cursor-pointer items-center pr-10 disabled:cursor-not-allowed disabled:opacity-60 aria-busy:cursor-not-allowed aria-busy:opacity-60"
        >
          {children}
        </select>
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-fg-3">
          <ChevronDownIcon />
        </span>
      </div>
      {hint ? (
        <p id={`${fieldId}-hint`} className="hint">
          {hint}
        </p>
      ) : null}
      {error ? <FieldError id={`${fieldId}-error`}>{error}</FieldError> : null}
    </div>
  );
}

export function Checkbox({
  label,
  error,
  id,
  ...props
}: { label: ReactNode; error?: string | undefined } & InputHTMLAttributes<HTMLInputElement>) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <div className="grid gap-1">
      <div className="check">
        <input
          id={fieldId}
          type="checkbox"
          {...props}
          aria-invalid={error ? true : undefined}
          aria-describedby={
            [props['aria-describedby'], error ? `${fieldId}-error` : null]
              .filter(Boolean)
              .join(' ') || undefined
          }
        />
        <label htmlFor={fieldId}>{label}</label>
      </div>
      {error ? (
        <p id={`${fieldId}-error`} className="text-[13px] text-bad">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** On and off at once (sheet: .toggle), for settings that apply without a save button. */
export function Switch({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="toggle">
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

/** One of a few, side by side (sheet: .seg), e.g. a period. */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  className = 'w-fit',
}: {
  label: string;
  value: T;
  options: readonly (readonly [T, ReactNode])[];
  onChange: (value: T) => void;
  /** Width: as wide as its options by default; "" lets a grid stretch it (overview.html). */
  className?: string;
}) {
  return (
    <div className={`seg ${className}`} role="group" aria-label={label}>
      {options.map(([key, text]) => (
        <button key={key} type="button" aria-pressed={key === value} onClick={() => onChange(key)}>
          {text}
        </button>
      ))}
    </div>
  );
}

/** A table that may scroll sideways on a phone (sheet: .tbl); the keyboard can reach and scroll it. */
export function TableBox({ label, children }: { label: string; children: ReactNode }) {
  return (
    // Focusable although not a control: a region that scrolls must be reachable by keyboard.
    <div className="tbl" tabIndex={0} role="region" aria-label={label}>
      {children}
    </div>
  );
}

/**
 * Message box of the sheet (.notice): the kind shows by its icon, not by a coloured edge (owner
 * 11.10). Errors are announced at once (role=alert), the rest politely (role=status).
 */
export function Notice({
  kind,
  title,
  action,
  children,
}: {
  kind: 'error' | 'success' | 'info' | 'warning';
  /** Bold first line, as in the sheet; `children` is then the text below it. */
  title?: ReactNode;
  /** A button at the end of the notice (sheet: .notice .btn), below the text on phones. */
  action?: ReactNode;
  children?: ReactNode;
}) {
  const icon = kind === 'success' ? <CheckIcon /> : kind === 'info' ? <ClockIcon /> : <AlertIcon />;
  return (
    <div
      role={kind === 'error' ? 'alert' : 'status'}
      className={`notice ${kind}${action ? '' : ' grid-cols-[auto_minmax(0,1fr)]'}`}
    >
      <span className="ico [&>svg]:h-full [&>svg]:w-full">{icon}</span>
      <div>
        {title ? (
          <>
            <b>{title}</b>
            {children ? <p>{children}</p> : null}
          </>
        ) : (
          children
        )}
      </div>
      {action ?? null}
    </div>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <section className={`card ${className}`}>{children}</section>;
}

/** Loading: a few skeleton lines (sheet: .skel), announced as text. */
/** `t`: the label in the page's language where that is not the app's (public status page). */
export function Loading({ t: fixedT }: { t?: TFunction } = {}) {
  const { t: appT } = useTranslation();
  const t = fixedT ?? appT;
  return (
    <div role="status" className="grid gap-2 py-6">
      <span className="sr-only">{t('app.loading')}</span>
      <div className="skel w-2/3" aria-hidden="true" />
      <div className="skel" aria-hidden="true" />
      <div className="skel w-1/2" aria-hidden="true" />
    </div>
  );
}

export function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <Notice kind="error">
      <p>{message}</p>
      <Button variant="secondary" size="sm" className="mt-2" onClick={onRetry}>
        {t('app.retry')}
      </Button>
    </Notice>
  );
}

export type MonitorState =
  | 'unknown'
  | 'up'
  | 'suspect'
  | 'down'
  | 'degraded'
  | 'paused'
  /** Not a monitor state: a bot protection hides the site until the customer allows us in. */
  | 'setup';

/**
 * State in words on a badge of the sheet, never colour alone; the dot pulses only while the site
 * is down (behavior.md: only an open incident pulses; overview.html: "Offline").
 */
export function StatusBadge({ state }: { state: MonitorState }) {
  const { t } = useTranslation();
  const tone = {
    up: 'ok',
    down: 'bad live',
    suspect: 'warn',
    degraded: 'warn',
    paused: 'neutral',
    unknown: 'neutral',
    setup: 'acc',
  }[state];
  return <span className={`badge ${tone}`}>{t(`status.${state}`)}</span>;
}

export type Severity = 'critical' | 'high' | 'medium' | 'low' | 'info';

export type Urgency = 'now' | 'soon' | 'when_possible' | 'tip';

/** "Sofort" / "Bald" / "Bei Gelegenheit" (v2 §4.2): the priority a customer acts on. */
export function UrgencyBadge({ urgency }: { urgency: Urgency }) {
  const { t } = useTranslation();
  const tone = { now: 'bad', soon: 'warn', when_possible: 'neutral', tip: 'acc' }[urgency];
  return <span className={`badge ${tone} nodot`}>{t(`report.urgency.${urgency}`)}</span>;
}

/** Severity of a finding (sheet: .sev). */
export function SeverityBadge({ severity }: { severity: Severity }) {
  const { t } = useTranslation();
  return <span className={`sev ${severity}`}>{t(`severity.${severity}`)}</span>;
}
